import { KeyboardEvent, SyntheticEvent } from "react";
import type { TFunction } from "i18next";

import { APIListValuesResponse } from "shared/api/api";
import { MAX_FILTER_ID, MIN_FILTER_ID } from "shared/constants";
import { excludeNulls } from "shared/utils";

import {
  EMPTY_FILTER_VALUE,
  FilterSchemaItem,
  FilterType,
} from "features/ui/Filters/types";
import { getMinMaxSelectOptions } from "features/ui/Filters/utils";
import { SelectOption } from "features/ui/Select";
import { toast } from "features/ui/Toast";

import { SelectFilterProps, SelectFilterState } from "./types";

/**
 * Determine the message to display when there are no search results
 */
export const getNoResultsMessage = (
  t: TFunction,
  {
    loading,
    options,
    search,
    input,
    emptyOptionsMessage,
  }: Partial<SelectFilterState & SelectFilterProps & FilterSchemaItem>
): string => {
  // Display "Loading..." message if data is still loading
  if (loading) {
    return t("messages.loading");
  }

  // Display "No results" message if options are loaded and empty
  // note that if we're using freeSolo prop, this never actually displays and it expected not to.
  // see https://github.com/mui/material-ui/issues/37862
  if (options?.length === 0) {
    return emptyOptionsMessage ?? t("common:no_results");
  }

  // Display "Type to search or paste values" message if search is active but input is empty
  if (search && !input) {
    return t("messages.type_to_search");
  }

  // Display "No results" message if search is active and input is provided, or if search is not active
  return t("common:no_results");
};

/**
 * Transform options based on filterType and enableMinMaxFilters flag
 * so that we can change "min" and "max" to their respective values based on filterType
 * and EMPTY_FILTER_VALUE to "Empty" or custom label
 */
export const transformOptions = (
  t: TFunction,
  options: SelectOption[],
  filterType?: FilterType,
  enableMinMaxFilters?: boolean,
  includeEmptyOption?: boolean | string
): SelectOption[] =>
  options.map((option) => {
    // Transform MIN/MAX options
    if (
      enableMinMaxFilters &&
      filterType &&
      [MIN_FILTER_ID, MAX_FILTER_ID].includes(option.id as string)
    ) {
      const minMaxOptions = getMinMaxSelectOptions(t, filterType) || [];

      return minMaxOptions.find(({ id }) => id === option.id) || option;
    }

    // Transform EMPTY_FILTER_VALUE option
    if (includeEmptyOption && option.id === EMPTY_FILTER_VALUE) {
      const emptyLabel =
        typeof includeEmptyOption === "string"
          ? includeEmptyOption
          : t("empty");

      return {
        id: EMPTY_FILTER_VALUE,
        value: emptyLabel,
      };
    }

    return option;
  });

/**
 * Get available options for the select dropdown, by automatically adding special options:
 * - MIN/MAX options (based on filterType and enableMinMaxFilters flag)
 * - Empty/No value option (based on includeEmptyOption flag)
 */
export const getOptions = (
  t: TFunction,
  options: SelectOption[],
  filterType?: FilterType,
  enableMinMaxFilters?: boolean,
  includeEmptyOption?: boolean | string
) => {
  let modifiedOptions = [...options];

  // Add "MIN" and "MAX" options to the beginning of the list
  if (enableMinMaxFilters && filterType) {
    const minMaxOptions = getMinMaxSelectOptions(t, filterType) || [];
    modifiedOptions = [...minMaxOptions, ...options];
  }

  // Add "Empty" option to the beginning of the list (after MIN/MAX if they exist)
  if (includeEmptyOption) {
    const emptyLabel =
      typeof includeEmptyOption === "string" ? includeEmptyOption : t("empty");
    const emptyOption = {
      id: EMPTY_FILTER_VALUE,
      value: emptyLabel,
    };
    modifiedOptions = [emptyOption, ...modifiedOptions];
  }

  return modifiedOptions;
};

/**
 * Removes "Add " from the value when selecting the "Add 'xyz'" option
 */
export const NEW_ITEM_OPTION_ID = "new-item-to-add";
export const getNewItemOption = (
  t: TFunction,
  value: string,
  isSingle: boolean
): SelectOption => ({
  id: NEW_ITEM_OPTION_ID,
  value: isSingle
    ? t("add_value_press_enter", { value })
    : t("add_value", { value }),
});

export const modifySelectedValue = (option: SelectOption): SelectOption => {
  if (option.id === NEW_ITEM_OPTION_ID) {
    // The display value wraps the raw entry in quotes (e.g. Add "xyz"); extract
    // it so this stays correct regardless of the (translated) surrounding text.
    const cleanValue =
      option.value.toString().match(/"([^"]*)"/)?.[1] ??
      option.value.toString();

    return {
      id: cleanValue,
      value: cleanValue,
    };
  }

  return option;
};

export const handleSelectAllAndCopyKeyDown = async (
  t: TFunction,
  event: KeyboardEvent,
  values: string[]
) => {
  const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  const isSelectAll =
    (isMac && event.metaKey && event.key === "a") ||
    (!isMac && event.ctrlKey && event.key === "a");
  const isCopy =
    (isMac && event.metaKey && event.key === "c") ||
    (!isMac && event.ctrlKey && event.key === "c");

  if (isSelectAll) {
    event.preventDefault();
  }

  if (isCopy) {
    event.preventDefault();
    if (values.length) {
      const textToCopy = values.join(",");
      try {
        await navigator.clipboard.writeText(textToCopy);
        toast.success(t("messages.copied_to_clipboard"));
      } catch (err) {
        console.error("Clipboard write failed", err);
      }
    }
  }
};

// Keeps falsy-but-valid values like 0; only nulls are dropped. A plain
// `.filter(Boolean)` here would silently discard a legitimate 0 option.
export const mapDistinctValuesToOptions = (
  distinctValues: APIListValuesResponse["distinctValues"] = []
): SelectOption[] =>
  excludeNulls(distinctValues).map((value) => ({ id: value, value }));

export const combineApiAndSelectedOptions = (
  apiOptions: SelectOption[],
  selectedOptions: SelectOption[]
) => {
  // Remove empty values
  const validAPIOptions = apiOptions.filter(
    (option) => !["null", ""].includes(option.id.toString())
  );

  // First options in a dropdown should be the selected ones
  const combinedOptions = [...selectedOptions];
  const selectedOptionIds = new Set(
    selectedOptions.map(({ id }) => String(id))
  );

  validAPIOptions.forEach((apiOption) => {
    if (!selectedOptionIds.has(String(apiOption.id))) {
      combinedOptions.push(apiOption);
    }
  });

  return combinedOptions;
};

interface OnChangeHandlerParams {
  handleOnChange: (values: SelectOption[]) => void;
  setInitialSelectedOption: (values: SelectOption[]) => void;
  maxValues?: number;
}

export const createOnChangeHandler =
  ({
    handleOnChange,
    setInitialSelectedOption,
    maxValues,
  }: OnChangeHandlerParams) =>
  (
    _: SyntheticEvent<Element, Event> | null,
    value: string | SelectOption | (string | SelectOption)[] | null
  ) => {
    if (!value) {
      handleOnChange([]);
      setInitialSelectedOption([]);
    } else {
      let values: SelectOption[] = [];
      if (typeof value === "string") {
        values.push({ id: value, value, label: value });
      } else if (Array.isArray(value)) {
        values = value.map((val) =>
          typeof val === "string"
            ? {
                id: val,
                value: val,
                label: val,
              }
            : modifySelectedValue(val)
        );
      } else {
        values.push(modifySelectedValue(value));
      }

      if (!maxValues || values.length <= maxValues) {
        handleOnChange(values);
        setInitialSelectedOption(values);
      }
    }
  };
